import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface ServiceCatalog {
  id: string;
  name: string;
  code: string;
  description?: string;
  serviceType: string;
  /** Legacy department grouping code. Optional since the field was removed from the form. */
  departmentCode?: string;
  price: number;
  taxPercent: number;
  isActive: boolean;
  requiresDoctor: boolean;
  /** How `price` is interpreted: flat | per_unit | per_hour | per_day. */
  billingUnit?: 'flat' | 'per_unit' | 'per_hour' | 'per_day';
  /** Display label for one unit (hour, day, session, ...). */
  unitLabel?: string;
  /** Minimum billable units (e.g. a 1-hour minimum). 0 = none. */
  minimumUnits?: number;
  /** Hourly rounding granularity in minutes. 0 = no rounding. */
  unitIncrement?: number;
}

/** A managed service type (the dynamic options for the Service Type field). */
export interface ServiceType {
  id: string;
  /** Stable identifier stored on `ServiceCatalog.serviceType`. */
  code: string;
  /** Human-readable label shown in the UI. */
  name: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  updatedAt?: string;
}

export const serviceService = {
  getServices: (page = 1, limit = 20, search?: string, serviceType?: string): Promise<ApiResponse<ServiceCatalog[]> & { meta: PaginationMeta; stats?: { trashed?: number } }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (serviceType) params.set('serviceType', serviceType);
    return get<ServiceCatalog[]>(`/services?${params.toString()}`) as Promise<ApiResponse<ServiceCatalog[]> & { meta: PaginationMeta; stats?: { trashed?: number } }>;
  },

  /** List soft-deleted services (the trash view). */
  getTrashedServices: (page = 1, limit = 20, search?: string): Promise<ApiResponse<ServiceCatalog[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<ServiceCatalog[]>(`/services/trash?${params.toString()}`) as Promise<ApiResponse<ServiceCatalog[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed services back to the active list. */
  restoreServices: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post('/services/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed services. */
  permanentlyDeleteServices: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string; table?: string }[] }>> => {
    return post('/services/trash/bulk-delete', { ids });
  },

  /** Permanently delete trashed services together with every linked record. */
  forceDeleteServices: (ids: string[]): Promise<
    ApiResponse<{
      deleted: string[];
      notFound: string[];
      purged?: Record<string, number>;
      blocked?: { id: string; reason: string; table?: string }[];
    }>
  > => {
    return post('/services/trash/bulk-force-delete', { ids });
  },

  getService: (id: string): Promise<ApiResponse<ServiceCatalog>> => {
    return get<ServiceCatalog>(`/services/${id}`);
  },

  createService: (data: any): Promise<ApiResponse<ServiceCatalog>> => {
    return post<ServiceCatalog>('/services', data);
  },

  updateService: (id: string, data: any): Promise<ApiResponse<ServiceCatalog>> => {
    return patch<ServiceCatalog>(`/services/${id}`, data);
  },

  deleteService: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/services/${id}`);
  },

  getServiceTypes: (page = 1, limit = 20, search?: string): Promise<ApiResponse<ServiceType[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<ServiceType[]>(`/service-types?${params.toString()}`) as Promise<ApiResponse<ServiceType[]> & { meta: PaginationMeta }>;
  },

  getActiveServiceTypes: (): Promise<ApiResponse<ServiceType[]>> => {
    return get<ServiceType[]>('/service-types/active/all');
  },

  getServiceType: (id: string): Promise<ApiResponse<ServiceType>> => {
    return get<ServiceType>(`/service-types/${id}`);
  },

  createServiceType: (data: any): Promise<ApiResponse<ServiceType>> => {
    return post<ServiceType>('/service-types', data);
  },

  updateServiceType: (id: string, data: any): Promise<ApiResponse<ServiceType>> => {
    return patch<ServiceType>(`/service-types/${id}`, data);
  },

  deleteServiceType: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/service-types/${id}`);
  },
};
